import { cva } from "class-variance-authority";
import { cn } from "cn";
import type { ComponentProps, FC } from "react";
import React from "react";

import type { ReadonlyRecord } from "@core/foundation/types";

import { Button as BaseButton } from "../shadcn/ui/button.tsx";

export * from "../shadcn/ui/button.tsx";

type BaseSize = NonNullable<ComponentProps<typeof BaseButton>["size"]>;

type Size = "cta" | BaseSize;

const rectangularVariants = cva<{
  readonly rectangular: { readonly true: string };
}>("", {
  defaultVariants: { rectangular: false },
  variants: { rectangular: { true: "rounded-none" } },
});

const roundVariants = cva<{ readonly round: { readonly true: string } }>("", {
  defaultVariants: { round: false },
  variants: { round: { true: "rounded-full" } },
});

const sizeVariants = cva<{ readonly size: ReadonlyRecord<Size, string> }>("", {
  defaultVariants: { size: "default" },
  variants: {
    size: {
      "cta": "h-12 rounded-xl",
      "default": "rounded-md",
      "icon": "rounded-md",
      "icon-lg": "rounded-lg",
      "icon-sm": "rounded-md",
      "icon-xs": "rounded-md",
      "lg": "rounded-lg",
      "sm": `
        rounded-md
        [&_svg]:size-3.5
      `,
      "xs": "rounded-md",
    },
  },
});

function toBaseSize(size: Size | undefined): BaseSize | undefined {
  switch (size) {
    case "cta":
    case undefined:
      return undefined;
    case "default":
    case "icon":
    case "icon-lg":
    case "icon-sm":
    case "icon-xs":
    case "lg":
    case "sm":
    case "xs":
      return size;
  }
}

interface ButtonProps extends Omit<ComponentProps<typeof BaseButton>, "size"> {
  readonly rectangular?: boolean;
  readonly round?: boolean;
  readonly size?: Size;
}

export const Button: FC<ButtonProps> = ({
  className,
  rectangular,
  round,
  size,
  ...props
}) => (
  <BaseButton
    className={cn(
      "cursor-pointer",
      sizeVariants({ size }),
      rectangularVariants({ rectangular }),
      roundVariants({ round }),
      className,
    )}
    size={toBaseSize(size)}
    {...props}
  />
);
